/**
 * Clinic portal order sheet (用紙発注) hooks.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useClinicContext } from './useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { getOrderSheetInit, submitOrderSheet } from '@/services/main/orderSheet';
import type {
  OrderSheetRowState,
  OrderSheetSubmitPayload,
  RezeptSheetItem,
} from '@/pages/main/orderSheet/types';
import { useTheme } from '@/shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';

function toInteger(value: string | number | null | undefined): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
}

/** Normalize API init response to sheets array and delivery fee number. */
function normalizeInitData(data: unknown): {
  sheets: RezeptSheetItem[];
  deliveryFeeMaster: number;
} {
  const raw = data && typeof data === 'object' && 'data' in data ? (data as { data: unknown }).data : null;
  if (!raw || typeof raw !== 'object') return { sheets: [], deliveryFeeMaster: 0 };
  const o = raw as { rezept_sheets?: unknown; delivery_fee?: number };
  const rawSheets = o.rezept_sheets;
  let sheets: RezeptSheetItem[] = [];
  if (Array.isArray(rawSheets)) {
    sheets = rawSheets;
  } else if (rawSheets && typeof rawSheets === 'object' && 'data' in rawSheets) {
    const inner = (rawSheets as { data: unknown }).data;
    sheets = Array.isArray(inner) ? inner : [];
  } else if (rawSheets && typeof rawSheets === 'object' && !Array.isArray(rawSheets)) {
    sheets = Object.values(rawSheets) as RezeptSheetItem[];
  }
  return { sheets, deliveryFeeMaster: Number(o.delivery_fee) || 0 };
}

/**
 * Fetches order form init data. Uses theme serviceType (A2/R2); refetches when it changes.
 * Returns normalized sheets and deliveryFeeMaster for consumption by useOrderSheetForm.
 */
export const useOrderSheetInit = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();
  const effectiveType = serviceType === SERVICE_TYPE_A2 ? SERVICE_TYPE_A2 : SERVICE_TYPE_R2;

  const { data, isLoading, isError, isPending, refetch, error } = useQuery({
    queryKey: ['order-sheets', 'init', tenant, clinicNumber, effectiveType],
    queryFn: () => getOrderSheetInit(tenant, clinicNumber, effectiveType),
    enabled: !!tenant && !!clinicNumber,
    staleTime: 30_000,
  });

  const { sheets, deliveryFeeMaster } = useMemo(
    () => (data ? normalizeInitData(data) : { sheets: [], deliveryFeeMaster: 0 }),
    [data]
  );

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return {
    data,
    sheets,
    deliveryFeeMaster,
    isLoading,
    isError,
    isPending,
    refetch,
    error,
  };
};

/**
 * Submits order. Toast on success/error in onSuccess/onError.
 */
export const useOrderSheetSubmit = () => {
  const { tenant, clinicNumber } = useClinicContext();
  const toast = useAppToast();

  const { mutateAsync: submitMutation, isPending: isLoading } = useMutation({
    mutationFn: (payload: OrderSheetSubmitPayload) =>
      submitOrderSheet(tenant, clinicNumber, payload),
    onSuccess: response => {
      if (response.success) {
        toast.success(response.message ?? '更新が正常に終了しました。');
      } else {
        toast.error(response.message ?? '更新ができませんでした。');
      }
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, '更新ができませんでした。'));
    },
  });

  return { submitMutation, isLoading };
};

function buildInitialRowState(): OrderSheetRowState {
  return {
    checked: false,
    quantity: undefined,
    requestDeliveryDate: null,
    isIncludeCase: false,
    remarks: '',
    deliveryFee: 0,
    datePickerKey: 0,
  };
}

export type OrderSheetFormHandlers = {
  handleSheetCheck: (sheet: RezeptSheetItem, checked: boolean) => void;
  handleQuantityChange: (id: number, value: number | undefined) => void;
  handleDeliveryDateChange: (id: number, value: string | null) => void;
  handleIncludeCaseCheck: (id: number, checked: boolean) => void;
  handleRemarksChange: (id: number, value: string) => void;
};

/**
 * Encapsulates order sheet form state: row state, totals, payload building, and validation.
 * Resets row state when service type (A2/R2) changes.
 */
export const useOrderSheetForm = (
  sheets: RezeptSheetItem[],
  deliveryFeeMaster: number
) => {
  const { serviceType } = useTheme();
  const effectiveType = serviceType === SERVICE_TYPE_A2 ? SERVICE_TYPE_A2 : SERVICE_TYPE_R2;

  const [rowState, setRowState] = useState<Record<number, OrderSheetRowState>>({});

  useEffect(() => {
    setRowState({});
  }, [effectiveType]);

  const setStateForId = useCallback((id: number, patch: Partial<OrderSheetRowState>) => {
    setRowState(prev => ({
      ...prev,
      [id]: { ...buildInitialRowState(), ...prev[id], ...patch },
    }));
  }, []);

  /**
   * Recompute per-row deliveryFee per BD §13 + A2 legacy logic:
   * - Each delivery day has only 1 row that bears 送料
   * - priorityId (row just edited) always claims its date
   * - Other rows: prioritize by sortno ASC, id ASC (sheets are already sorted by BE)
   * - Row not eligible (not checked / case同梱 / no date) -> fee = 0
   */
  const recomputeFees = useCallback(
    (
      state: Record<number, OrderSheetRowState>,
      priorityId?: number
    ): Record<number, OrderSheetRowState> => {
      const next = { ...state };
      const dateOwners = new Set<string>();

      if (priorityId != null) {
        const r = next[priorityId];
        if (r?.checked && r.requestDeliveryDate && !r.isIncludeCase) {
          dateOwners.add(r.requestDeliveryDate);
        }
      }

      for (const sheet of sheets) {
        if (sheet.id === priorityId) continue;
        const r = next[sheet.id];
        if (!r) continue;
        const eligible = r.checked && !!r.requestDeliveryDate && !r.isIncludeCase;
        if (!eligible) {
          if (r.deliveryFee !== 0) next[sheet.id] = { ...r, deliveryFee: 0 };
          continue;
        }
        const date = r.requestDeliveryDate as string;
        if (dateOwners.has(date)) {
          if (r.deliveryFee !== 0) next[sheet.id] = { ...r, deliveryFee: 0 };
        } else {
          dateOwners.add(date);
          if (r.deliveryFee !== deliveryFeeMaster) {
            next[sheet.id] = { ...r, deliveryFee: deliveryFeeMaster };
          }
        }
      }

      if (priorityId != null) {
        const r = next[priorityId];
        if (r) {
          const eligible = r.checked && !!r.requestDeliveryDate && !r.isIncludeCase;
          const expectedFee = eligible ? deliveryFeeMaster : 0;
          if (r.deliveryFee !== expectedFee) {
            next[priorityId] = { ...r, deliveryFee: expectedFee };
          }
        }
      }

      return next;
    },
    [sheets, deliveryFeeMaster]
  );

  const handleSheetCheck = useCallback(
    (sheet: RezeptSheetItem, checked: boolean) => {
      setRowState(prev => {
        const next = { ...prev };
        if (checked) {
          next[sheet.id] = {
            ...buildInitialRowState(),
            checked: true,
            quantity: 1,
          };
        } else {
          next[sheet.id] = buildInitialRowState();
        }
        return recomputeFees(next);
      });
    },
    [recomputeFees]
  );

  const handleQuantityChange = useCallback(
    (id: number, value: number | undefined) => {
      setStateForId(id, { quantity: value });
    },
    [setStateForId]
  );

  const handleDeliveryDateChange = useCallback(
    (id: number, dateStr: string | null) => {
      setRowState(prev => {
        const next = { ...prev };
        const current = { ...buildInitialRowState(), ...next[id] };
        const needsAutoAdjust = !dateStr || dateStr <= dayjs().format('YYYY-MM-DD');
        const adjusted = needsAutoAdjust
        ? dayjs().add(1, 'day').format('YYYY-MM-DD')
        : dateStr;
        if (needsAutoAdjust) {
          current.datePickerKey = (current.datePickerKey ?? 0) + 1;
        }
        current.requestDeliveryDate = adjusted;
        next[id] = current;
        return recomputeFees(next, id);
      });
    },
    [recomputeFees]
  );

  const handleIncludeCaseCheck = useCallback((id: number, checked: boolean) => {
    setRowState(prev => {
      const next = { ...prev };
      const current = { ...buildInitialRowState(), ...next[id] };
      current.isIncludeCase = checked;
      if (checked) {
        current.requestDeliveryDate = null;
      }
      next[id] = current;
      return recomputeFees(next);
    });
  }, [recomputeFees]);

  const handleRemarksChange = useCallback(
    (id: number, value: string) => {
      setStateForId(id, { remarks: value.slice(0, 255) });
    },
    [setStateForId]
  );

  const totals = useMemo(() => {
    let st = 0;
    const datesWithFee = new Set<string>();
    let dt = 0;
    sheets.forEach(sheet => {
      const row = rowState[sheet.id];
      if (!row?.checked || row.quantity == null || row.quantity < 1) return;
      st += row.quantity * sheet.price;
      if (row.requestDeliveryDate && !row.isIncludeCase) {
        if (!datesWithFee.has(row.requestDeliveryDate)) {
          datesWithFee.add(row.requestDeliveryDate);
          dt += deliveryFeeMaster;
        }
      }
    });
    return { subtotal: st, deliveryTotal: dt, total: st + dt };
  }, [sheets, rowState, deliveryFeeMaster]);

  const buildPayload = useCallback((): OrderSheetSubmitPayload | null => {
    const ids: number[] = [];
    const payload: OrderSheetSubmitPayload = {
      rezept_sheet_ids: [],
      subtotal: 0,
      delivery_fee: 0,
      total: 0,
    };
    const datesWithFee = new Set<string>();
    sheets.forEach(sheet => {
      const row = rowState[sheet.id];
      if (!row?.checked || row.quantity == null || row.quantity < 1) return;
      ids.push(sheet.id);
      let rowDeliveryFee = 0;
      if (row.requestDeliveryDate && !row.isIncludeCase) {
        if (!datesWithFee.has(row.requestDeliveryDate)) {
          datesWithFee.add(row.requestDeliveryDate);
          rowDeliveryFee = deliveryFeeMaster;
        }
      }
      payload[`quantity_${sheet.id}`] = row.quantity;
      payload[`price_${sheet.id}`] = sheet.price;
      payload[`sheet_number_${sheet.id}`] = sheet.sheet_number;
      payload[`sheet_name_${sheet.id}`] = sheet.sheet_name;
      payload[`unit_amount_${sheet.id}`] = toInteger(sheet.unit_amount);
      payload[`delivery_fee_${sheet.id}`] = rowDeliveryFee;
      payload[`is_include_case_${sheet.id}`] = row.isIncludeCase ? 1 : 0;
      payload[`request_delivery_date_${sheet.id}`] = row.requestDeliveryDate ?? '';
      payload[`remarks_${sheet.id}`] = row.remarks;
    });
    if (ids.length === 0) return null;
    payload.rezept_sheet_ids = ids;
    payload.subtotal = totals.subtotal;
    payload.delivery_fee = totals.deliveryTotal;
    payload.total = totals.total;
    return payload;
  }, [sheets, rowState, totals, deliveryFeeMaster]);

  const validateForSubmit = useCallback((): { ok: true } | { ok: false; message: string } => {
    if (totals.subtotal < 1) {
      return { ok: false, message: '購入したい用紙のチェックを行い数量を指定してください。' };
    }
    const missingDateOrCase = sheets.some(sheet => {
      const row = rowState[sheet.id];
      if (!row?.checked) return false;
      return !row.isIncludeCase && !row.requestDeliveryDate;
    });
    if (missingDateOrCase) {
      return { ok: false, message: '納品希望日を入力してください' };
    }
    return { ok: true };
  }, [sheets, rowState, totals.subtotal]);

  const handlers: OrderSheetFormHandlers = useMemo(
    () => ({
      handleSheetCheck,
      handleQuantityChange,
      handleDeliveryDateChange,
      handleIncludeCaseCheck,
      handleRemarksChange,
    }),
    [
      handleSheetCheck,
      handleQuantityChange,
      handleDeliveryDateChange,
      handleIncludeCaseCheck,
      handleRemarksChange,
    ]
  );

  return {
    rowState,
    setRowState,
    handlers,
    totals,
    buildPayload,
    validateForSubmit,
    buildInitialRowState,
  };
};
